import allFilters from '@/components/Table/Filters';
import { getFiltersMap } from '@/components/Table/utils';

export type FilterOperator = string;

export type AidaParamOverride = {
  aidaKeys: string[];
  column: string;
  operator: FilterOperator;
  scope: 'evidence' | 'summary' | 'both';
  tabs?: string[];
  views?: string[];
  requireIdent?: boolean;
  normalize?: (value: string) => string;
  includeInLabel?: boolean;
  labelFormat?: (value: string) => string;
};

const SKIP_KEYS = new Set([
  'order_by',
  'order',
  'page',
  'page_number',
  'page_size',
  'pageNumber',
  'pageSize',
  'source',
  'format',
  'output',
  'results',
  'gene',
  'gene_symbol',
  'gene_ncbi_id',
  'gene_ncbi_id_filter',
  'disease',
  'disease_filter',
  'variant',
  'variant_name',
  'chem_ids_init',
  'chem_ids_filter',
]);

const EVIDENCE_ONLY_COLUMNS = new Set([
  'associationType',
  'ancestry_name',
  'disease_model',
  'disease_model_in_doc',
  'sex_in_doc',
  'sentenceHTML',
  'pmid',
  'pmYear',
  'journal_abbrv',
  'beta',
  'odds_ratio',
  'chemical_effect',
  'reference',
  'reference_type',
  'association',
  'numberChemsIncludedInEvidence',
  'chemsInSentence',
]);

function stripUmlsPrefix(value: string): string {
  return value.replace(/^UMLS_/i, '');
}

function isNumericYear(value: string): boolean {
  return /^\d{4}$/.test(value.trim()) || /^\d+$/.test(value.trim());
}

function isNumericScore(value: string): boolean {
  return /^-?\d+(?:\.\d+)?$/.test(value.trim());
}

function looksLikeDiseaseCui(value: string): boolean {
  return /^(?:UMLS_)?C\d+$/i.test(value.trim());
}

function looksLikeGeneNcbiId(value: string): boolean {
  return /^\d+$/.test(value.trim());
}

export const AIDA_OVERRIDES: AidaParamOverride[] = [
  {
    aidaKeys: ['gene_ncbi_id', 'gene_ncbi_id_filter'],
    column: 'geneNcbiID',
    operator: 'equals',
    scope: 'both',
    tabs: ['GDA'],
    views: ['DISEASES', 'ALL'],
    requireIdent: true,
  },
  {
    aidaKeys: ['disease', 'disease_filter'],
    column: 'diseaseUMLSCUI',
    operator: 'equals',
    scope: 'both',
    tabs: ['GDA'],
    views: ['GENES'],
    requireIdent: true,
    normalize: stripUmlsPrefix,
  },
  {
    aidaKeys: ['disease_model', 'disease_model_in_doc', 'dmt', 'dmt_doc'],
    column: 'disease_model_in_doc',
    operator: 'eq',
    scope: 'evidence',
    includeInLabel: true,
  },
  {
    aidaKeys: ['sex', 'sex_in_doc', 'sex_doc', 'gender'],
    column: 'sex_in_doc',
    operator: 'eq',
    scope: 'evidence',
    includeInLabel: true,
  },
  {
    aidaKeys: ['association_type', 'associationType'],
    column: 'associationType',
    operator: 'eq',
    scope: 'evidence',
    includeInLabel: true,
  },
  {
    aidaKeys: ['historical_score_year_max', 'year_max'],
    column: 'pmYear',
    operator: 'lte',
    scope: 'evidence',
  },
  {
    aidaKeys: ['historical_score_year_min', 'year_min'],
    column: 'pmYear',
    operator: 'gte',
    scope: 'evidence',
  },
  {
    aidaKeys: ['min_score', 'score'],
    column: 'score',
    operator: 'gte',
    scope: 'both',
    includeInLabel: true,
    labelFormat: v => `score ≥ ${v}`,
  },
  {
    aidaKeys: ['max_score'],
    column: 'score',
    operator: 'lte',
    scope: 'both',
    includeInLabel: true,
    labelFormat: v => `score ≤ ${v}`,
  },
];

type FilterMeta = { operator: FilterOperator; type?: string };

type ResolvedFilter = {
  column: string;
  operator: FilterOperator;
  scope: 'evidence' | 'summary' | 'both';
  value: string;
  aidaKey: string;
  includeInLabel?: boolean;
  labelFormat?: (value: string) => string;
};

function buildColumnMeta(): Map<string, FilterMeta> {
  const map = new Map<string, FilterMeta>();
  for (const filter of allFilters as Array<{
    name: string;
    operator: string;
    type?: string;
    disable?: boolean;
  }>) {
    if (filter.disable) continue;
    if (!map.has(filter.name)) {
      map.set(filter.name, { operator: filter.operator, type: filter.type });
    }
  }
  return map;
}

function buildApiParamToColumns(): Map<string, string[]> {
  const inverted = new Map<string, string[]>();
  for (const [column, apiParam] of getFiltersMap().entries()) {
    const list = inverted.get(apiParam) ?? [];
    if (!list.includes(column)) list.push(column);
    inverted.set(apiParam, list);
  }
  return inverted;
}

const COLUMN_META = buildColumnMeta();
const API_PARAM_TO_COLUMNS = buildApiParamToColumns();

const OVERRIDE_CLAIMED_KEYS = new Set(AIDA_OVERRIDES.flatMap(o => o.aidaKeys));

function firstNonEmptyValue(
  params: Record<string, string[]>,
  keys: string[],
): { key: string; value: string } | null {
  for (const key of keys) {
    const values = params[key];
    if (!values) continue;
    for (const raw of values) {
      const value = String(raw).trim();
      if (value) return { key, value };
    }
  }
  return null;
}

function pickColumnForApiParam(apiParam: string, view: string): string | null {
  const candidates = API_PARAM_TO_COLUMNS.get(apiParam);
  if (!candidates?.length) return null;

  const inFilters = candidates.filter(c => COLUMN_META.has(c));
  const pool = inFilters.length > 0 ? inFilters : candidates;

  if (pool.length === 1) return pool[0];

  if (view === 'GENES') {
    const geneCol = pool.find(c => /gene/i.test(c));
    if (geneCol) return geneCol;
  }
  if (view === 'VARIANTS') {
    const variantCol = pool.find(c => /variant/i.test(c) || c === 'DSI' || c === 'DPI');
    if (variantCol) return variantCol;
  }
  if (view === 'DISEASES') {
    const diseaseCol = pool.find(c => /disease/i.test(c));
    if (diseaseCol) return diseaseCol;
  }

  return pool[0];
}

function valuePassesIdentCheck(override: AidaParamOverride, aidaKey: string, value: string): boolean {
  if (!override.requireIdent) return true;
  if (aidaKey === 'disease' || aidaKey === 'disease_filter') return looksLikeDiseaseCui(value);
  if (aidaKey === 'gene_ncbi_id' || aidaKey === 'gene_ncbi_id_filter') return looksLikeGeneNcbiId(value);
  return true;
}

function valuePassesTypeCheck(column: string, operator: string, value: string): boolean {
  if (column === 'pmYear') return isNumericYear(value);
  if (column === 'score' && (operator === 'gte' || operator === 'lte' || operator === 'eq')) {
    return isNumericScore(value);
  }
  return true;
}

function tablePrefixFor(
  scope: 'evidence' | 'summary' | 'both',
  column: string,
  isEvidence: boolean,
): 'evidence' | 'summary' {
  if (scope === 'evidence' || EVIDENCE_ONLY_COLUMNS.has(column)) return 'evidence';
  if (scope === 'summary') return 'summary';
  return isEvidence ? 'evidence' : 'summary';
}

function resolveOverride(
  params: Record<string, string[]>,
  override: AidaParamOverride,
  ctx: { tab: string; view: string },
): ResolvedFilter | null {
  if (override.tabs && !override.tabs.includes(ctx.tab)) return null;
  if (override.views && !override.views.includes(ctx.view)) return null;

  const hit = firstNonEmptyValue(params, override.aidaKeys);
  if (!hit) return null;
  if (!valuePassesIdentCheck(override, hit.key, hit.value)) return null;
  if (!valuePassesTypeCheck(override.column, override.operator, hit.value)) return null;

  const value = override.normalize ? override.normalize(hit.value) : hit.value;
  return {
    column: override.column,
    operator: override.operator,
    scope: override.scope,
    value,
    aidaKey: hit.key,
    includeInLabel: override.includeInLabel,
    labelFormat: override.labelFormat,
  };
}

function resolveAutoKey(aidaKey: string, value: string, view: string): ResolvedFilter | null {
  if (SKIP_KEYS.has(aidaKey) || OVERRIDE_CLAIMED_KEYS.has(aidaKey)) return null;

  // Exact column name as used in the UI / URL
  if (COLUMN_META.has(aidaKey)) {
    const meta = COLUMN_META.get(aidaKey)!;
    if (!valuePassesTypeCheck(aidaKey, meta.operator, value)) return null;
    return {
      column: aidaKey,
      operator: meta.operator,
      scope: EVIDENCE_ONLY_COLUMNS.has(aidaKey) ? 'evidence' : 'both',
      value,
      aidaKey,
      includeInLabel: meta.type === 'select',
    };
  }

  const column = pickColumnForApiParam(aidaKey, view);
  if (!column) return null;

  const meta = COLUMN_META.get(column);
  const operator = meta?.operator ?? 'eq';
  if (!valuePassesTypeCheck(column, operator, value)) return null;

  return {
    column,
    operator,
    scope: EVIDENCE_ONLY_COLUMNS.has(column) ? 'evidence' : 'both',
    value,
    aidaKey,
    includeInLabel: meta?.type === 'select',
  };
}

export type BuildFiltersContext = {
  params?: Record<string, string[]>;
  tab: string;
  view: string;
  isEvidence: boolean;
};

export function buildFiltersFromAidaParams(ctx: BuildFiltersContext): URLSearchParams {
  const extra = new URLSearchParams();
  const { params, tab, view, isEvidence } = ctx;
  if (!params) return extra;

  const appliedColumnOps = new Set<string>();
  const consumedKeys = new Set<string>();

  const apply = (resolved: ResolvedFilter) => {
    const columnOpKey = `${resolved.column}-${resolved.operator}`;
    if (appliedColumnOps.has(columnOpKey)) return;
    const prefix = tablePrefixFor(resolved.scope, resolved.column, isEvidence);
    extra.set(`filters-${prefix}-${resolved.column}-${resolved.operator}`, resolved.value);
    appliedColumnOps.add(columnOpKey);
    consumedKeys.add(resolved.aidaKey);
  };

  for (const override of AIDA_OVERRIDES) {
    const resolved = resolveOverride(params, override, { tab, view });
    if (resolved) {
      apply(resolved);
      for (const key of override.aidaKeys) {
        if (params[key]) consumedKeys.add(key);
      }
    }
  }

  for (const [aidaKey, values] of Object.entries(params)) {
    if (consumedKeys.has(aidaKey) || SKIP_KEYS.has(aidaKey)) continue;
    const value = values.map(v => String(v).trim()).find(Boolean);
    if (!value) continue;

    const resolved = resolveAutoKey(aidaKey, value, view);
    if (resolved) apply(resolved);
  }

  return extra;
}

export function collectAidaFilterLabelFragments(
  params?: Record<string, string[]>,
  rawParams?: Record<string, string[]>,
): string[] {
  const fragments: string[] = [];
  if (!params && !rawParams) return fragments;

  const canonical = params ?? {};
  const fallback = rawParams ?? {};

  const pick = (keys: string[]): string | null => {
    const fromParams = firstNonEmptyValue(canonical, keys);
    if (fromParams) return fromParams.value;
    const fromRaw = firstNonEmptyValue(fallback, keys);
    return fromRaw?.value ?? null;
  };

  for (const override of AIDA_OVERRIDES) {
    if (!override.includeInLabel) continue;
    const value = pick(override.aidaKeys);
    if (!value) continue;
    if (override.column === 'score' && !isNumericScore(value)) continue;
    fragments.push(override.labelFormat ? override.labelFormat(value) : value);
  }

  for (const [aidaKey, values] of Object.entries(canonical)) {
    if (SKIP_KEYS.has(aidaKey) || OVERRIDE_CLAIMED_KEYS.has(aidaKey)) continue;
    const value = values.map(v => String(v).trim()).find(Boolean);
    if (!value) continue;
    const resolved = resolveAutoKey(aidaKey, value, 'ALL');
    if (!resolved?.includeInLabel) continue;
    const labelValue = pick([aidaKey]) ?? value;
    if (!fragments.includes(labelValue)) fragments.push(labelValue);
  }

  return fragments;
}
